'use client'

import { useAuthorization } from '@entities/authentication'
import {
  attemptKrPaymentOrder,
  cancelKrPaymentOrder,
  getKrPaymentOrder,
  getActiveKrPaymentOrder,
  prepareKrPaymentOrder,
  recoverKrPaymentOrder,
  resumeKrBillingOrder,
  confirmKrPaymentOrder,
} from '@entities/kr-payment/api'
import {
  assertKrOrderMatches,
  isKrOrderEditable,
  isKrOrderRetired,
  clearKrPaymentReturn,
  krOrderResumePath,
  isKrProviderCancelled,
  isKrPreparationPriceError,
  prepareKrPaymentOrderRequestSchema,
  type KrPaymentOrder,
  type KrOrderShipping,
  type PrepareKrPaymentOrderRequest,
} from '@entities/kr-payment/model'
import type { PaymentMethodType } from '@entities/payment-methods'
import { isPaymentCanceled } from '@features/payment/lib/is-payment-canceled'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useRef } from 'react'
import { z } from 'zod'

const storedIntentSchema = z.object({
  key: z.string().min(1),
  orderId: z.string().nullable(),
  request: prepareKrPaymentOrderRequestSchema.optional(),
})

const rejectedPreparationSchema = z.object({
  status: z.number().int().min(400).max(499),
  code: z.enum([
    'INVALID_PRODUCT',
    'INVALID_PRODUCT_DETAIL_TARGET',
    'INVALID_PAYMENT',
    'INVALID_PAYMENT_AMOUNT',
    'COUPON_NOT_FOUND',
  ]),
})

interface Options {
  enabled: boolean
  subscribeId: string
  productId?: string | null
  amount: number
  couponId: string | null
  paymentMethod: PaymentMethodType | null
  isLumpSum: boolean
  shipping?: KrOrderShipping
  expectedTotalLessonCount?: number
  expectedValidityMonths?: number
  captureTrialContext?: boolean
  trialLevel?: 'B' | 'C1' | 'C2' | 'D' | 'SMART_TALK'
  onPending: (orderId: string) => void
  onPreparationPriceRejected?: () => Promise<void>
}

export function useKrPreparedPayment(options: Options) {
  const { uid, bearerToken } = useAuthorization()
  const onPending = options.onPending
  const searchParams = useSearchParams()
  const router = useRouter()
  const intent = useRef<z.infer<typeof storedIntentSchema> | null>(null)
  const pending = useRef<Promise<KrPaymentOrder | null> | null>(null)
  const retiredOrderIds = useRef(new Set<string>())
  const storageKey = `podo:kr-purchase:${uid}:${options.subscribeId}`
  const loadedStorageKey = useRef(storageKey)

  const restoreOriginalOrder = (order: KrPaymentOrder) => {
    // A soft navigation can retain edited coupon/method state in this same checkout component.
    window.location.replace(krOrderResumePath(order))
  }

  const prepareUnboundOrder = async (request: PrepareKrPaymentOrderRequest) => {
    try {
      return options.captureTrialContext === true
        ? await prepareKrPaymentOrder(request, bearerToken, { captureTrialContext: true })
        : await prepareKrPaymentOrder(request, bearerToken)
    } catch (error) {
      // Preparation never charges. Only a definitive rejection may retire an intent that has no known order.
      // Unknown network/server outcomes must keep the same key so a created order cannot be orphaned.
      if (intent.current?.orderId === null && rejectedPreparationSchema.safeParse(error).success) {
        sessionStorage.removeItem(storageKey)
        intent.current = null
        if (isKrPreparationPriceError(error)) {
          try {
            await options.onPreparationPriceRejected?.()
          } catch {
            // The definitive rejection remains authoritative if a read-only refresh is unavailable.
          }
        }
      }
      throw error
    }
  }

  const prepare = async (claim = false): Promise<KrPaymentOrder | null> => {
    if (!options.productId) {
      throw new Error('KR_PAYMENT_PRODUCT_REQUIRED')
    }
    if (loadedStorageKey.current !== storageKey) {
      intent.current = null
      loadedStorageKey.current = storageKey
    }
    if (!intent.current) {
      const saved = sessionStorage.getItem(storageKey)
      const parsed = saved ? storedIntentSchema.safeParse(JSON.parse(saved)) : null
      intent.current = parsed?.success ? parsed.data : { key: crypto.randomUUID(), orderId: null }
      const redirectedOrderId = searchParams.get('purchase_order_id')
      if (redirectedOrderId && !retiredOrderIds.current.has(redirectedOrderId)) {
        intent.current.orderId = redirectedOrderId
      }
      // Store before the network request: timeout must reuse the same server idempotency key.
      sessionStorage.setItem(storageKey, JSON.stringify(intent.current))
    }
    const paymentMethod = options.paymentMethod?.includes('KAKAO')
      ? 'KAKAO'
      : options.paymentMethod?.includes('NAVER')
        ? 'NAVER'
        : options.isLumpSum
          ? 'TOSS'
          : 'CARD'
    const request: PrepareKrPaymentOrderRequest = {
      subscribeId: options.subscribeId,
      expectedProductId: options.productId,
      expectedAmount: options.amount,
      couponId: options.couponId,
      paymentMethod,
      idempotencyKey: intent.current.key,
      trialLevel: options.trialLevel,
      ...(options.shipping ? { shipping: options.shipping } : {}),
      ...(options.expectedTotalLessonCount !== undefined
        ? { expectedTotalLessonCount: options.expectedTotalLessonCount }
        : {}),
      ...(options.expectedValidityMonths !== undefined
        ? { expectedValidityMonths: options.expectedValidityMonths }
        : {}),
    }
    if (!intent.current.orderId && !intent.current.request) {
      const active = await getActiveKrPaymentOrder(bearerToken)
      if (active && !isKrOrderRetired(active)) {
        intent.current.orderId = active.orderId
      }
    }
    intent.current.request ??= request
    sessionStorage.setItem(storageKey, JSON.stringify(intent.current))
    const hasKnownOrder = Boolean(intent.current.orderId)
    let order = intent.current.orderId
      ? await getKrPaymentOrder(intent.current.orderId, bearerToken)
      : await prepareUnboundOrder(intent.current.request)
    let hasChangedRequest = false
    if (isKrOrderEditable(order)) {
      try {
        assertKrOrderMatches(order, request)
      } catch {
        hasChangedRequest = true
      }
    }
    if (
      hasKnownOrder &&
      isKrOrderEditable(order) &&
      (hasChangedRequest || (order.status === 'PROCESSING' && Date.parse(order.expiresAt) <= Date.now()))
    ) {
      // The server atomically retires the old attempt and keeps its late-approval journal.
      intent.current = { key: crypto.randomUUID(), orderId: null }
      request.idempotencyKey = intent.current.key
      request.replacesOrderId = order.orderId
      intent.current.request = request
      sessionStorage.setItem(storageKey, JSON.stringify(intent.current))
      order = await prepareUnboundOrder(request)
    }
    if (order.status === 'PROCESSING') {
      const recovery = await recoverKrPaymentOrder(order.orderId, bearerToken)
      order = recovery.order
      if (order.subscribeId !== options.subscribeId) {
        router.replace(krOrderResumePath(order))
        return null
      }
      if (recovery.action === 'RESUME_CONFIRM' && options.isLumpSum && order.authorityMode === 'MANUAL_CONFIRM_V2') {
        try {
          assertKrOrderMatches(order, request)
        } catch {
          restoreOriginalOrder(order)
          return null
        }
        try {
          await confirmKrPaymentOrder(order.orderId, bearerToken)
        } catch {
          /* Reconcile without authorizing another SDK attempt. */
        }
        onPending(order.orderId)
        return null
      }
      if (recovery.action === 'RESUME_BILLING' && !claim && !options.isLumpSum) {
        if (
          order.productId !== request.expectedProductId ||
          order.paymentAmount !== request.expectedAmount ||
          (order.couponId ?? null) !== request.couponId
        ) {
          restoreOriginalOrder(order)
          return null
        }
        // Only the server journal supplies the original billing key; never re-register or read it from browser state.
        try {
          await resumeKrBillingOrder(order.orderId, bearerToken)
        } catch {
          /* Outcome stays unknown; reconcile the same order below. */
        }
        onPending(order.orderId)
        return null
      }
      if (recovery.action === 'RESUME_DIRECT' && claim && options.isLumpSum && order.canExecute) {
        assertKrOrderMatches(order, request)
        return order
      }
      onPending(order.orderId)
      return null
    }
    if (
      order.subscribeId !== options.subscribeId &&
      order.status !== 'FAILED' &&
      !isKrOrderEditable(order) &&
      !isKrOrderRetired(order)
    ) {
      router.replace(krOrderResumePath(order))
      return null
    }
    if ((order.status === 'FAILED' || isKrOrderRetired(order)) && searchParams.get('purchase_order_id')) {
      retiredOrderIds.current.add(order.orderId)
      sessionStorage.removeItem(storageKey)
      intent.current = null
      const url = clearKrPaymentReturn(new URL(window.location.href))
      router.replace(`${url.pathname}${url.search}`, { scroll: false })
      return null
    }
    if (
      order.status === 'FAILED' ||
      isKrOrderRetired(order) ||
      (order.status === 'PREPARED' && Date.parse(order.expiresAt) <= Date.now())
    ) {
      intent.current = { key: crypto.randomUUID(), orderId: null }
      request.idempotencyKey = intent.current.key
      intent.current.request = request
      sessionStorage.setItem(storageKey, JSON.stringify(intent.current))
      order = await prepareUnboundOrder(request)
    }
    intent.current.orderId = order.orderId
    sessionStorage.setItem(storageKey, JSON.stringify(intent.current))
    if (request.replacesOrderId && searchParams.get('purchase_order_id') === request.replacesOrderId) {
      const url = clearKrPaymentReturn(new URL(window.location.href))
      url.searchParams.set('purchase_order_id', order.orderId)
      if (order.couponId) {
        url.searchParams.set('coupon_id', order.couponId)
      } else {
        url.searchParams.delete('coupon_id')
      }
      window.history.replaceState(window.history.state, '', url)
    }
    if (order.status !== 'PREPARED') {
      if (order.status === 'FAILED') {
        throw new Error('KR_PAYMENT_ORDER_FAILED')
      }
      onPending(order.orderId)
      return null
    }
    // A return from the provider uses the already authenticated server snapshot.
    assertKrOrderMatches(
      order,
      searchParams.get('purchase_order_id') ? { ...request, paymentMethod: order.paymentMethod } : request,
    )
    if (!claim) {
      return order
    }
    const attempt = await attemptKrPaymentOrder(order.orderId, bearerToken)
    assertKrOrderMatches(attempt, request)
    if (!attempt.canExecute) {
      onPending(attempt.orderId)
      return null
    }
    return attempt
  }

  const cancel = useCallback(
    async (attemptOrderId?: string) => {
      const orderId = attemptOrderId ?? intent.current?.orderId ?? searchParams.get('purchase_order_id')
      if (!orderId) {
        return
      }
      try {
        const order = await cancelKrPaymentOrder(orderId, bearerToken)
        // A late response for an old SDK window must never discard the new checkout.
        const currentUrl = new URL(window.location.href)
        const ownsIntent =
          intent.current?.orderId === orderId || (!intent.current && searchParams.get('purchase_order_id') === orderId)
        if (!ownsIntent) {
          return
        }
        if (order.status === 'FAILED' || isKrOrderRetired(order)) {
          retiredOrderIds.current.add(orderId)
          const saved = sessionStorage.getItem(storageKey)
          const parsed = saved ? storedIntentSchema.safeParse(JSON.parse(saved)) : null
          if (parsed?.success && parsed.data.orderId === orderId) {
            sessionStorage.removeItem(storageKey)
          }
          intent.current = null
          if (currentUrl.searchParams.get('purchase_order_id') === orderId) {
            const url = clearKrPaymentReturn(new URL(window.location.href))
            window.history.replaceState(window.history.state, '', url)
          }
        } else {
          onPending(orderId)
        }
      } catch {
        // Unknown cancellation cannot retire an order or supersede a newer local checkout.
        if (
          intent.current?.orderId === orderId ||
          (!intent.current && searchParams.get('purchase_order_id') === orderId)
        ) {
          onPending(orderId)
        }
      }
    },
    [bearerToken, storageKey, searchParams, onPending],
  )
  const cancelledReturn = useRef<string | null>(null)
  const returnedOrderId = searchParams.get('purchase_order_id')
  const isCancelledReturn =
    isKrProviderCancelled({
      purchase_order_id: returnedOrderId ?? undefined,
      code: searchParams.get('code') ?? undefined,
      imp_success: searchParams.get('imp_success') ?? undefined,
    }) ||
    isPaymentCanceled({
      code: searchParams.get('code') ?? undefined,
      message: searchParams.get('message') ?? undefined,
      error_code: searchParams.get('error_code') ?? undefined,
      error_msg: searchParams.get('error_msg') ?? undefined,
    })
  useEffect(() => {
    if (options.enabled && returnedOrderId && isCancelledReturn && cancelledReturn.current !== returnedOrderId) {
      cancelledReturn.current = returnedOrderId
      void cancel(returnedOrderId)
    }
  }, [options.enabled, returnedOrderId, isCancelledReturn, cancel])

  if (!options.enabled) {
    return undefined
  }
  const preparePayment = (claim = false) => {
    if (pending.current) {
      return pending.current.then((order) => {
        if (order) {
          onPending(order.orderId)
        }
        return null
      })
    }
    pending.current = prepare(claim).finally(() => {
      pending.current = null
    })
    return pending.current
  }
  return Object.assign(preparePayment, { cancel })
}
